// Illustratieve visuals in de Nextconvert-stijl (donkerblauw + oranje)
// Nagebouwd naar het referentievoorbeeld, volledig met CSS/SVG.

const Chip = ({
  children,
  className = "",
  accent = false,
}: {
  children: React.ReactNode;
  className?: string;
  accent?: boolean;
}) => (
  <span
    className={`absolute text-[9px] md:text-[10px] font-semibold px-2 py-1 rounded-md whitespace-nowrap ${
      accent
        ? "bg-accent text-accent-foreground"
        : "bg-white/10 text-white/80"
    } ${className}`}
  >
    {children}
  </span>
);

const Cursor = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 12 14"
    className={`absolute w-2.5 h-3 text-accent fill-current ${className}`}
    aria-hidden="true"
  >
    <path d="M0 0l12 5.5-5 1.4L4.6 14z" />
  </svg>
);

const Frame = ({ children }: { children: React.ReactNode }) => (
  <div className="relative w-full h-full bg-primary overflow-hidden">
    {children}
  </div>
);

/* 01 — Onderzoek */
export const VisualOnderzoek = () => (
  <Frame>
    <div className="absolute inset-x-0 top-1/2 h-px bg-white/12" />
    <div className="absolute inset-y-0 left-1/2 w-px bg-white/12" />
    <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
      <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-white/80" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
      </svg>
    </div>
    <Chip accent className="left-3 top-3">Desires</Chip>
    <Cursor className="left-[70px] top-[34px]" />
    <Chip className="right-3 top-3">Pain Points</Chip>
    <Cursor className="right-[86px] top-[36px] rotate-[200deg]" />
    <Chip className="left-3 bottom-3">Emotional Triggers</Chip>
    <Cursor className="left-[54px] bottom-[36px] rotate-[200deg]" />
    <Chip accent className="right-3 bottom-3">Objections</Chip>
    <Cursor className="right-[70px] bottom-[34px]" />
  </Frame>
);

/* 02 — Design */
export const VisualDesign = () => (
  <Frame>
    <div className="absolute inset-4 rounded-md bg-white/[0.04]" />
    <div className="absolute left-8 top-6 w-[38%] h-[46%] border border-dashed border-white/25 rounded-sm" />
    <div className="absolute right-8 top-1/2 -translate-y-1/2 w-[34%] h-[42%] border border-dashed border-accent rounded-sm">
      {["-left-[3px] -top-[3px]", "-right-[3px] -top-[3px]", "-left-[3px] -bottom-[3px]", "-right-[3px] -bottom-[3px]"].map(
        (pos) => (
          <span key={pos} className={`absolute ${pos} w-1.5 h-1.5 bg-accent rounded-[1px]`} />
        )
      )}
      <Cursor className="right-3 bottom-2 rotate-[10deg]" />
    </div>
    <Chip className="left-2 top-[38%]">USP&apos;s</Chip>
    <Chip accent className="right-6 bottom-3">CTA</Chip>
  </Frame>
);

/* 03 — Ontwikkeling */
export const VisualOntwikkeling = () => (
  <Frame>
    <div className="absolute left-6 right-4 top-9 bottom-2 rounded-md bg-white/[0.06] p-3 overflow-hidden">
      <div className="space-y-1.5 font-mono text-[7px] md:text-[8px] text-white/45 leading-relaxed">
        <p className="pl-4">.then((response) =&gt; response.json())</p>
        <p className="pl-6">.then((data) =&gt; &#123;</p>
        <p className="pl-10">if (cartCountElement) &#123;</p>
        <p className="pl-14 text-white/25">cartCountElement.textContent =</p>
        <p className="pl-4 text-white/25">data.item_count || 0;</p>
      </div>
    </div>
    <Cursor className="right-10 bottom-8 rotate-[10deg]" />
    <Chip accent className="right-3 bottom-3">JS</Chip>
  </Frame>
);

/* 04 — Analyseren en herhalen */
export const VisualAnalyseren = () => (
  <Frame>
    <div className="absolute left-4 bottom-4 flex items-end gap-1.5 h-[55%]">
      {[52, 34, 74, 60].map((h, i) => (
        <div key={i} className="w-4 md:w-5 bg-white/12 rounded-[2px]" style={{ height: `${h}%` }} />
      ))}
    </div>
    <Chip className="left-3 top-[34%] !px-1.5 !py-0.5">+5.4%</Chip>
    <Chip className="left-[34px] bottom-[26px] !px-1.5 !py-0.5">+4.2%</Chip>
    <Chip className="left-[66px] top-[24%] !px-1.5 !py-0.5">+6.7%</Chip>
    <div className="absolute right-3 top-5 bottom-6 w-[48%] border border-dashed border-white/25 rounded-sm" />
    <Chip className="right-8 top-2">Repeat</Chip>
    <Chip accent className="right-[92px] top-[46%] !px-1.5 !py-0.5">+5.4%</Chip>
    <Chip className="right-4 top-[32%] !px-1.5 !py-0.5">+2.1%</Chip>
    <svg viewBox="0 0 40 24" className="absolute right-[46px] top-[44%] w-8 h-5 text-accent" fill="none" stroke="currentColor" strokeWidth="2.5">
      <path d="M6 16h20a6 6 0 000-12H14" strokeLinecap="round" />
      <path d="M10 11L5 16l5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </Frame>
);

export const benefitVisuals = [
  VisualOnderzoek,
  VisualDesign,
  VisualOntwikkeling,
  VisualAnalyseren,
];
